import { UseFormReturnType } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import {
  CountrySelector,
  LabelWithTooltip,
  MultiSelect,
  NumberInput,
  Select,
  Switch,
  TextInput,
} from "@tfg/ui"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { defaultUpdatePermissions } from "~/pages/Brand/Suppliers/SuppliersRepository/utils/useFormFieldConfig"
import { ContactDrawer } from "~/pages/SupplierPortal/TraceabilityRequests/components/ContactDrawer"
import useContactForm from "~/pages/SupplierPortal/Contacts/hooks/useContactForm"
import { SelectListRegistrationIdField } from "~/components/SelectListRegistrationIdField"
import { ProductionSiteRepositoryAutocompleteField } from "~/pages/SupplierPortal/ProductionSites/components/ProductionSiteRepositoryAutocompleteField"
import { useAuth } from "~/store/auth-context"
import { COUNTRIES } from "~/utils/countries"
import {
  applyCreatedBusinessPartnerSelection,
  createdBusinessPartnerSelectionFromResult,
} from "~/utils/zod/apply-created-business-partner-selection"
import { useSchemaFormNestedViewContext } from "~/utils/zod/schema-form-nested-view-context"

import type { SchemaFieldRenderer } from "./schema-form.types"

interface BaseProps {
  readonly name: string
  readonly label: string
  readonly tooltip: string | undefined
  readonly isRequired: boolean
  readonly placeholder: string | undefined
  readonly autoComplete: string | undefined
  readonly disabled: boolean | undefined
  readonly error: string | undefined
  readonly form: UseFormReturnType<Record<string, unknown>>
}

const PRODUCTION_SITE_LINKED_READ_ONLY_FIELDS = new Set<string>([
  "parentCompany",
  "internalCode",
  "performedActivities",
  "registrationIds",
  "street",
  "city",
  "postalCode",
  "longitude",
  "latitude",
  "countryIso3166",
])

const isLinkedRepositoryRecord = (value: unknown): boolean => {
  return typeof value === "string" && value.trim().length > 0
}

const resolveFieldDisabledState = ({
  name,
  disabled,
  form,
}: {
  readonly name: string
  readonly disabled: boolean | undefined
  readonly form: UseFormReturnType<Record<string, unknown>>
}): boolean => {
  if (disabled === true) {
    return true
  }

  const repositoryRecordId = form.values.repositoryRecordId
  const isInLinkedMode = isLinkedRepositoryRecord(repositoryRecordId)

  return isInLinkedMode && PRODUCTION_SITE_LINKED_READ_ONLY_FIELDS.has(name)
}

const HiddenFieldRenderer: SchemaFieldRenderer = () => null

interface TextFieldRendererProps extends BaseProps {}

const TextFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  tooltip,
  placeholder,
  autoComplete,
  disabled,
  isRequired,
  error,
  form,
}: TextFieldRendererProps) => {
  const inputProps = form.getInputProps(name)
  const fieldDisabled = resolveFieldDisabledState({ name, disabled, form })
  return (
    <TextInput
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
          w="100%"
          justify="space-between"
        />
      }
      labelProps={{
        w: "100%",
      }}
      w="100%"
      placeholder={placeholder}
      autoComplete={autoComplete}
      disabled={fieldDisabled}
      {...inputProps}
      error={error}
      onChange={(event) => {
        form.setFieldValue(name, event.currentTarget.value)
      }}
    />
  )
}

interface NumberFieldRendererProps extends BaseProps {}

const NumberFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  tooltip,
  isRequired,
  placeholder,
  autoComplete,
  disabled,
  error,
  form,
}: NumberFieldRendererProps) => {
  const inputProps = form.getInputProps(name)
  const fieldDisabled = resolveFieldDisabledState({ name, disabled, form })
  return (
    <NumberInput
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
          w="100%"
        />
      }
      labelProps={{
        w: "100%",
      }}
      placeholder={placeholder}
      autoComplete={autoComplete}
      disabled={fieldDisabled}
      {...inputProps}
      error={error}
      onChange={(value) => {
        form.setFieldValue(name, value)
      }}
    />
  )
}

interface BooleanFieldRendererProps extends BaseProps {}

const BooleanFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  isRequired,
  tooltip,
  disabled,
  error,
  form,
}: BooleanFieldRendererProps) => {
  const inputProps = form.getInputProps(name, { type: "checkbox" })
  const fieldDisabled = resolveFieldDisabledState({ name, disabled, form })
  return (
    <Switch
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
          w="100%"
        />
      }
      disabled={fieldDisabled}
      {...inputProps}
      error={error}
    />
  )
}

interface SelectFieldRendererProps extends BaseProps {
  readonly options:
    | ReadonlyArray<{
        readonly label: string
        readonly value: string
      }>
    | undefined
  readonly creatable?: boolean
  readonly createLabel?: string
}

const SelectFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  isRequired,
  tooltip,
  placeholder,
  autoComplete,
  disabled,
  options,
  creatable,
  createLabel,
  error,
  form,
}: SelectFieldRendererProps) => {
  const inputProps = form.getInputProps(name)
  const fieldDisabled = resolveFieldDisabledState({ name, disabled, form })
  return (
    <Select
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
        />
      }
      labelProps={{
        w: "100%",
      }}
      placeholder={placeholder}
      autoComplete={autoComplete}
      disabled={fieldDisabled}
      searchable
      clearable
      creatable={creatable}
      createLabel={createLabel}
      data={
        options?.map((opt) => ({ label: opt.label, value: opt.value })) ?? []
      }
      {...inputProps}
      error={error}
      errorOnTouch={false}
      onChange={(value) => {
        form.setFieldValue(name, value)
      }}
    />
  )
}

interface BusinessPartnerSelectFieldRendererProps extends SelectFieldRendererProps {}

const BusinessPartnerSelectFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  isRequired,
  tooltip,
  placeholder,
  autoComplete,
  disabled,
  options,
  creatable,
  createLabel,
  error,
  form,
}: BusinessPartnerSelectFieldRendererProps) => {
  const { t } = useTranslation("organizationSettings")
  const nestedViewContext = useSchemaFormNestedViewContext()
  const isBusinessPartnerCreateHosted =
    nestedViewContext?.isBusinessPartnerCreateHosted === true
  const [isContactDrawerOpen, contactDrawerActions] = useDisclosure(false)
  const [search, setSearch] = useState("")
  const createdBusinessPartnerRef = useRef<{
    id: string
    name: string
  } | null>(null)
  const { user } = useAuth()
  const organizationId = user?.organization?.id ?? ""
  const {
    contact,
    handleContact,
    reset: resetContactDraft,
  } = useContactForm({
    organizationId,
    onContactCreated: (result) => {
      if (isBusinessPartnerCreateHosted) {
        return
      }
      applyCreatedBusinessPartnerSelection({
        form,
        fieldName: name,
        selection: createdBusinessPartnerSelectionFromResult(result),
        createdBusinessPartnerRef,
      })
    },
  })

  useEffect(() => {
    if (!isBusinessPartnerCreateHosted || nestedViewContext === null) {
      return
    }
    return nestedViewContext.registerBusinessPartnerCreatedHandler(
      (selection) => {
        applyCreatedBusinessPartnerSelection({
          form,
          fieldName: name,
          selection,
          createdBusinessPartnerRef,
        })
      }
    )
  }, [form, isBusinessPartnerCreateHosted, name, nestedViewContext])
  const inputProps = form.getInputProps(name)
  const fieldDisabled = resolveFieldDisabledState({ name, disabled, form })
  const selectedBusinessRelationshipId =
    typeof inputProps.value === "string" ? inputProps.value : ""
  const selectData = useMemo(() => {
    const baseOptions =
      options?.map((opt) => ({ label: opt.label, value: opt.value })) ?? []
    const cached = createdBusinessPartnerRef.current
    if (
      cached !== null &&
      selectedBusinessRelationshipId === cached.id &&
      baseOptions.every((option) => option.value !== cached.id)
    ) {
      return [...baseOptions, { label: cached.name, value: cached.id }]
    }
    return baseOptions
  }, [options, selectedBusinessRelationshipId])
  const handleCreateBusinessPartner = useCallback(() => {
    if (isBusinessPartnerCreateHosted && nestedViewContext !== null) {
      nestedViewContext.openCreateBusinessPartnerView()
      return
    }
    resetContactDraft()
    contactDrawerActions.open()
  }, [
    contactDrawerActions,
    isBusinessPartnerCreateHosted,
    nestedViewContext,
    resetContactDraft,
  ])

  return (
    <>
      <Select
        label={
          <LabelWithTooltip
            label={label}
            hoverHelperText={tooltip}
            withAsterisk={isRequired}
          />
        }
        labelProps={{
          w: "100%",
        }}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={fieldDisabled}
        searchable
        clearable
        creatable={creatable && !fieldDisabled && organizationId !== ""}
        createLabel={createLabel ?? t("supplierSettings.addNewSupplier")}
        onCreate={handleCreateBusinessPartner}
        onDropdownOpen={() => setSearch("")}
        onSearchChange={setSearch}
        searchValue={search}
        data={selectData}
        errorOnTouch={false}
        {...inputProps}
        error={error ?? inputProps.error}
        onChange={(value) => {
          form.setFieldValue(name, value)
          form.validateField(name)
        }}
      />
      {!isBusinessPartnerCreateHosted && isContactDrawerOpen && (
        <ContactDrawer
          opened={isContactDrawerOpen}
          setModalOpened={contactDrawerActions}
          supplierMapping={contact}
          handleSupplierMapping={handleContact}
          isUpdate={false}
          updatePermissions={defaultUpdatePermissions}
        />
      )}
    </>
  )
}

interface MultiSelectFieldRendererProps extends BaseProps {
  readonly options:
    | ReadonlyArray<{
        readonly label: string
        readonly value: string
      }>
    | undefined
}

const MultiSelectFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  isRequired,
  tooltip,
  placeholder,
  disabled,
  options,
  error,
  form,
}: MultiSelectFieldRendererProps) => {
  const inputProps = form.getInputProps(name)
  const value = (inputProps.value as string[] | undefined) ?? []
  const fieldDisabled = resolveFieldDisabledState({ name, disabled, form })
  return (
    <MultiSelect
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
          w="100%"
        />
      }
      placeholder={placeholder}
      disabled={fieldDisabled}
      searchable
      size="xs"
      clearable
      labelProps={{
        w: "100%",
      }}
      data={
        options?.map((opt) => ({ label: opt.label, value: opt.value })) ?? []
      }
      value={value}
      error={error}
      onChange={(values) => {
        form.setFieldValue(name, values)
      }}
    />
  )
}

interface SelectListRegistrationIdRendererProps extends BaseProps {
  readonly options:
    | ReadonlyArray<{
        readonly label: string
        readonly value: string
        readonly description?: string
      }>
    | undefined
}

const SelectListRegistrationIdRenderer: SchemaFieldRenderer = ({
  name,
  label,
  tooltip,
  isRequired,
  placeholder,
  disabled,
  options,
  error: fieldError,
  form,
}: SelectListRegistrationIdRendererProps) => {
  const value =
    (form.values[name] as
      | { id?: string; registrationId: string; type: string }[]
      | undefined) ?? []
  const fieldDisabled = resolveFieldDisabledState({ name, disabled, form })

  return (
    <SelectListRegistrationIdField
      value={value}
      onChange={(list) => {
        form.setFieldValue(name, list)
      }}
      onValidate={() => undefined}
      options={options?.map((o) => ({ ...o })) ?? []}
      label={label}
      tooltip={tooltip}
      isRequired={isRequired}
      placeholder={placeholder}
      disabled={fieldDisabled}
      fieldError={fieldError}
    />
  )
}

interface CountrySelectFieldRendererProps extends BaseProps {}

const CountrySelectFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  tooltip,
  isRequired,
  disabled,
  placeholder,
  error,
  form,
}: CountrySelectFieldRendererProps) => {
  const inputProps = form.getInputProps(name)
  const fieldDisabled = resolveFieldDisabledState({ name, disabled, form })
  return (
    <CountrySelector
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
        />
      }
      labelProps={{
        w: "100%",
      }}
      searchable
      placeholder={placeholder}
      disabled={fieldDisabled}
      countriesMapper={COUNTRIES}
      {...inputProps}
      value={inputProps.value ?? null}
      error={error}
      onChange={(value) => {
        form.setFieldValue(name, value)
      }}
    />
  )
}

export const DEFAULT_FIELD_RENDERERS: Record<string, SchemaFieldRenderer> = {
  text: TextFieldRenderer,
  number: NumberFieldRenderer,
  boolean: BooleanFieldRenderer,
  select: SelectFieldRenderer,
  businessPartnerSelect: BusinessPartnerSelectFieldRenderer,
  multiSelect: MultiSelectFieldRenderer,
  selectListRegistrationId: SelectListRegistrationIdRenderer,
  countrySelect: CountrySelectFieldRenderer,
  productionSiteRepositoryAutocomplete:
    ProductionSiteRepositoryAutocompleteField,
  hidden: HiddenFieldRenderer,
}
